import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogCancelButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import { Input } from '@/components/ui-kit/input';
import { Textarea } from '@/components/ui-kit/textarea';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { usePayoffOvertime } from '../../hooks/use-overtime';
import {
  createPayoffFormSchema,
  PayoffFormValues,
  payoffSecondsOf,
} from '../../schemas/payoff-form-schema';
import { OvertimeRecord } from '../../types/overtime.types';
import { formatSecondsAsHoursMinutes } from '../../utils/format-duration';

export interface PayoffDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  overtime: OvertimeRecord | null;
}

export const PayoffDialog = ({ open, onOpenChange, overtime }: PayoffDialogProps) => {
  const { t } = useTranslation();
  const isNoteRequired = useHasPermission(HRM_PERMISSIONS.TIME_ATTENDANCE.OVERTIME_REQUIRE_PAYOFF_NOTE);
  const schema = useMemo(
    () => createPayoffFormSchema(overtime?.TotalRemainingOvertimeHourInSecond ?? 0, isNoteRequired),
    [overtime?.TotalRemainingOvertimeHourInSecond, isNoteRequired]
  );

  const { mutateAsync: payoffOvertime, isPending } = usePayoffOvertime();

  const form = useForm<PayoffFormValues>({
    resolver: async (data, context, options) => {
      const result = await zodResolver(schema)(data, context, options);
      if (result.errors) {
        result.errors = Object.fromEntries(
          Object.entries(result.errors).map(([key, error]) => [
            key,
            { ...error, message: t((error as { message?: string }).message ?? '') },
          ])
        );
      }
      return result;
    },
    defaultValues: { PayoffTime: '', Note: '' },
  });

  useEffect(() => {
    if (!open) return;
    form.reset({ PayoffTime: '', Note: '' });
  }, [open, form]);

  if (!overtime) return null;

  const handleSubmit = async (values: PayoffFormValues) => {
    try {
      await payoffOvertime({
        overtime,
        payoffSeconds: payoffSecondsOf(values.PayoffTime),
        note: values.Note,
      });
      onOpenChange(false);
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('PAY_OFF_OVERTIME')}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          {t('REMAINING_OVERTIME')}:{' '}
          {formatSecondsAsHoursMinutes(overtime.TotalRemainingOvertimeHourInSecond)}
        </p>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="PayoffTime"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('PAYOFF_TIME_HH_MM')}</FormLabel>
                  <FormControl>
                    {/* Hours:minutes of overtime, not a clock time, so a payoff can exceed 23:59. */}
                    <Input inputMode="numeric" placeholder="HH:MM" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isNoteRequired && (
              <FormField
                control={form.control}
                name="Note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('NOTE')}</FormLabel>
                    <FormControl>
                      <Textarea rows={3} placeholder={t('NOTE')} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('PAY_OFF')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
